import React, {useEffect, useState} from "react";
import {useAuth} from "../../../auth/AuthContext.js";
import {ChevronDown, ChevronUp, Sparkles, BarChart} from "lucide-react";

const SubQuestionTable = ({submissions, assignment_id, questions}) => {
    const [tableData, setTableData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const {user} = useAuth();

    useEffect(() => {
        if (questions.length > 0 && submissions.length > 0) {
            processData();
        }
    }, [questions, submissions, assignment_id]);

    const getSubQuestionLetter = (index) => {
        return String.fromCharCode(97 + index); // 97 is the ASCII code for 'a'
    };

    const processData = () => {
        const processedData = questions.flatMap((question, qIndex) =>
            question.subQuestions.map((subQ, subIndex) => ({
                questionNumber: qIndex + 1,
                questionName: question.description,
                subQuestionLetter: getSubQuestionLetter(subIndex),
                subQuestionID: subQ.id,
                subQuestionName: subQ.question,
                points: subQ.max_weight || 0,  // Use max_weight instead of score
                feedback: subQ.feedback,
                mean: 0,
            }))
        );
        calculateAverages(processedData);
    };

    const calculateAverages = (data) => {
        const scoresBySubQuestionId = {};

        submissions.forEach(submission => {
            Object.values(submission.feedback || {}).forEach(feedback => {
                if (!feedback) {
                    return;
                }

                const subQuestionId = feedback.assignment_subquestion_id;
                const value = feedback.score;

                // Initialize the array if it doesn't exist yet
                if (!scoresBySubQuestionId[subQuestionId]) {
                    scoresBySubQuestionId[subQuestionId] = [];
                }

                scoresBySubQuestionId[subQuestionId].push(value);
            });
        });

        const updatedData = data.map(item => {
            const scores = scoresBySubQuestionId[item.subQuestionID] || [];
            const mean = scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
            return {...item, mean};
        });

        setTableData(updatedData);
    };

    const toggleExpand = (index) => {
        setExpandedRows(prev => ({...prev, [index]: !prev[index]}));
    };

    // Helper function to calculate and format percentage
    const calculatePercentage = (mean, points) => {
        if (!points || points === 0) {
            return <span style={{color: 'gray'}}>0%</span>;
        }

        const percentage = (mean / points) * 100;
        let color = '';

        if (percentage >= 80) {
            color = 'green';
        } else if (percentage >= 40) {
            color = 'orange';
        } else {
            color = 'red';
        }

        return (
            <span style={{color}}>
            {percentage.toFixed(1)}%
        </span>
        );
    };

    // Helper function to truncate text to 200 characters
    const truncateText = (text, maxLength = 200) => {
        if (!text) {
            return '';
        }
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    return (
        <div className="w-full max-w-full">
            <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                <table className="w-full table-fixed divide-y divide-gray-200 max-w-full">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            style={{width: '50%'}}>Question
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            style={{width: '10%'}}>Score
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium p-2 uppercase tracking-wider" style={{
                            background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                            color: 'white',
                            width: '40%'
                        }}>
                            <div className="flex items-center">
                                <Sparkles size={16} className="mr-1 flex-shrink-0"/> <span className="truncate">AI Analysis</span>
                            </div>
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {tableData.map((item, index) => (
                        <React.Fragment key={index}>
                            <tr className="hover:bg-gray-50 transition-colors duration-150">
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">Q{item.questionNumber}</div>
                                    <div className="text-sm text-gray-500 whitespace-normal truncate"
                                         title={item.questionName}>
                                        {truncateText(item.questionName)}
                                    </div>

                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                    {calculatePercentage(item.mean, item.points)}
                                </td>
                                <td className="px-6 py-4 text-sm font-medium">
                                    {item.feedback ? (
                                        <div className="flex items-start justify-between w-full">
                                            <div
                                                className="text-xs cursor-pointer line-clamp-2 text-left transition-colors duration-200 hover:text-[#c99b69]"
                                                title="Click to view full AI analysis"
                                                style={{
                                                    color: '#8c6b4a',
                                                    width: 'calc(100% - 30px)',
                                                    paddingRight: '8px'
                                                }}
                                                onClick={() => toggleExpand(index)}
                                            >
                                                {item.feedback}
                                            </div>
                                            <button
                                                className="focus:outline-none ml-2 p-1 rounded-full transition-all duration-200 flex-shrink-0 hover:bg-[rgba(228,177,122,0.2)] hover:text-[#c99b69]"
                                                style={{
                                                    color: '#e4b17a',
                                                    backgroundColor: 'rgba(228, 177, 122, 0.1)'
                                                }}
                                                title="View full AI analysis"
                                                onClick={() => toggleExpand(index)}
                                            >
                                                {expandedRows[index] ? <ChevronUp size={18}/> :
                                                    <ChevronDown size={18}/>}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="text-xs italic" style={{color: '#a17c57'}}>
                                            Generate AI Analysis to view insights
                                        </div>
                                    )}
                                </td>
                            </tr>
                            {expandedRows[index] && (
                                <tr className="animate-fadeIn" style={{backgroundColor: '#fdf8f3'}}>
                                    <td colSpan="3" className="px-6 py-4">
                                        <div className="bg-white rounded-lg p-4 transition-all duration-300" style={{
                                            border: '1px solid rgba(228, 177, 122, 0.2)',
                                            boxShadow: 'rgba(228, 177, 122, 0.06) 0px 2px 4px -2px, rgba(228, 177, 122, 0.04) 0px 4px 8px -2px'
                                        }}>
                                            <div className="flex items-center mb-2">
                                                <Sparkles size={18} className="mr-2" style={{color: '#e4b17a'}}/>
                                                <h3 className="text-lg font-semibold" style={{color: '#8c6b4a'}}>AI
                                                    Analysis</h3>
                                            </div>
                                            <div className="flex space-x-4">
                                                <div className="w-1 rounded-full"
                                                     style={{background: 'linear-gradient(135deg, #e4b17a, #c99b69)'}}></div>
                                                <div
                                                    className="text-sm text-gray-800 leading-relaxed break-words overflow-hidden"
                                                    style={{whiteSpace: 'pre-wrap'}}
                                                >
                                                    {item.feedback ? item.feedback : "AI Analysis unavailable until grades published."}
                                                </div>
                                            </div>
                                            {item.feedback && (
                                                <div className="mt-3 flex justify-end">
                                                    <div className="text-xs px-2 py-1 rounded-md flex items-center"
                                                         style={{
                                                             backgroundColor: 'rgba(228, 177, 122, 0.1)',
                                                             color: '#8c6b4a'
                                                         }}>
                                                        <BarChart size={14} className="mr-1"/>
                                                        Average Score: {calculatePercentage(item.mean, item.points)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SubQuestionTable;